/**
 * Value-interior localization.
 *
 * `keywords` gives the renderer the command verb and `roleMarkers` gives it the
 * particle that frames a role, but a role's VALUE has an interior of its own —
 * the `true` in `set @disabled to true`, the `value` in `put my value into
 * #out`, the `seconds` in `wait 2 seconds`. Until the profile gained a
 * `lexicon` block there was nowhere for those words to come from, so the
 * renderer emitted them in English. The target-language parser then could not
 * bind them back and dropped the role silently.
 *
 * Measured 2026-08-26 across the corpus (161 examples x 23 languages): the
 * en->foreign render was 73.3% structurally clean against the English
 * reference where the i18n-rendered corpus was 97.0%, and 99.6% of that gap is
 * attributable to five value types. This module is what closes it.
 *
 * SCOPE — deliberately word-level, like the implementation it replaces.
 * @lokascript/i18n has localized value interiors this way for years
 * (`translateWord` / `translateMultiWordValue`), and the corpus proves the
 * output re-parses in all 23 languages. Nothing here tries to be cleverer than
 * that; it only makes the same capability reachable from a typed IR.
 *
 * WHAT IS NEVER TOUCHED — the reason this is safe to run over a raw expression:
 *   - anything inside quotes, backticks, or a template interpolation (user text)
 *   - a type name after `as` or `is a`/`is an` (TYPE_NAME)
 *   - anything inside a BRACE GROUP (`{name: 'Demo', admin: true}`, `.{cls}`):
 *     an object literal is data the runtime evaluates as JS, not prose. Measured
 *     2026-08-27: `true` inside one was localized on render (`admin: verdadero`)
 *     and never reversed on parse — the interior is captured as one opaque
 *     literal — so the round trip lost the value in all 23 languages AND a
 *     localized source would have evaluated an undefined identifier at runtime.
 *     A BARE `true` is unaffected: it round-trips, because the parse side does
 *     de-localize a standalone literal.
 *   - anything inside a BRACKET GROUP: an array literal (`[1, me]`), an
 *     attribute selector (`<[aria-pressed=true]/>`), an index or a range
 *     (`arr[idx]`, `var[(index-1)..(index+1)]`). Every tokenizer reads a bracket
 *     group whole, and no reader de-localizes inside one, so a word localized
 *     there stayed in the read-back, in all 23 languages: `2 is in [true, n]`
 *     came back from es as `2 is in [verdadero, n]`, an array holding a
 *     variable named `verdadero`, and `I match <[aria-pressed=true]/>` lost its
 *     selector, so its translation was refused. A WHOLE value that is a bracket
 *     group was already written as written; this is the same group inside a
 *     larger expression.
 *   - a CALL's arguments (`f(…)`, a `(` touching the name before it): every
 *     reader takes them as written, so es `sprayInto(yo)` called `sprayInto`
 *     with a variable named `yo`, in all 23 languages.
 *   - selectors, urls, numbers, and sigil-attached tokens (`@attr`, `#id`, `$var`)
 *   - identifiers the lexicon has no entry for (they pass through unchanged)
 * A word is rewritten only when the profile vouches for it, so an unknown token
 * is always left alone rather than guessed at.
 */
import type { LanguageLexicon, LanguageProfile } from '../generators/profiles/types';
import { getLexicon } from '../lexicon-registry';
import { conversionAsWord, queryInWord } from '../parser/utils/grammar-words';
import { elementScopeWord } from '../element-scope';

/** Category order matters: first hit wins, mirroring i18n's DICTIONARY_CATEGORIES. */
const LEXICON_CATEGORIES = ['values', 'expressions', 'logical', 'temporal', 'attributes'] as const;

/**
 * Spans that must survive verbatim: single/double/backtick strings, including
 * any interpolation inside them (that is code the author wrote, not vocabulary).
 */
const PROTECTED_SPAN = /"[^"]*"|'[^']*'|`[^`]*`/g;

/**
 * Private-use sentinels for masked spans. Hyperscript source cannot contain
 * these, so the mask/restore round-trip is exact — a bare digit in the source
 * (`wait 2 seconds`) can never be mistaken for a span index.
 */
const MASK_OPEN = '\uE010';
const MASK_CLOSE = '\uE011';
const MASK_TOKEN = /\uE010(\d+)\uE011/g;

/**
 * A localizable word: ASCII letters (with internal hyphens), at least two
 * characters, not attached to a sigil, a dot, or a digit. Deliberately
 * conservative — `my.value` and `#id` are handled by their own renderers, and a
 * one-letter token is far more likely a variable than a keyword.
 */
const WORD = /(^|[^\w$.#@*:-])([A-Za-z][A-Za-z-]+)(?![\w$.:-])/g;

/**
 * A TYPE NAME — what `as` converts to, what `is a`/`is an` checks — is the
 * runtime's vocabulary, not the language's: both engines read `Element`,
 * `Number`, `HTMLElement` in English. Localized (es `elemento`, ja `要素`, tr
 * `öğe`), it read back as a variable: `#a is an Element` became `#a is an
 * elemento`, false in all 18 languages whose lexicon names `element`, and `set
 * x to 7 as Element` lost its conversion (es) or became the string
 * `"7as要素"` (ja). `Number` survived only because no lexicon names it. A
 * capitalized name after `as`, or after `is a`/`is an`/`is not a`/`is not an`,
 * stays as written, with a `:` suffix (`Fixed:2`, `Values:Form`). PR 95. So
 * does one after `am a`, upstream's spelling for `I`: `if I am a Element` was
 * es `I am a elemento`. The `as` before it is written in the language's own
 * word (conversionLead).
 */
const TYPE_NAME =
  /(\bas\s+|\b(?:is|am)\s+(?:not\s+)?an?\s+)([A-Z][A-Za-z0-9_]*(?::[A-Za-z0-9_]+)?)\b/g;

export interface ValueLexicon {
  /** English word (lowercased) → the form to render in this language. */
  readonly words: ReadonlyMap<string, string>;
}

const cache = new WeakMap<LanguageLexicon, ValueLexicon>();

/**
 * Build the flat English→native map for a profile, merging `lexicon` categories
 * with `references` (me/it/you) and possessive adjectives, all of which appear
 * inside values.
 *
 * `lexicon.events` is deliberately NOT consulted. Event names already have a
 * dedicated renderer (`localizeEventName`) backed by a curated denylist of
 * events that must stay English to round-trip; localizing them a second time
 * here would bypass that decision.
 */
export function getValueLexicon(lexicon: LanguageLexicon, profile?: LanguageProfile): ValueLexicon {
  const cached = cache.get(lexicon);
  if (cached) return cached;

  const words = new Map<string, string>();
  const add = (english: string, native: string | undefined): void => {
    if (!native) return;
    const key = english.toLowerCase();
    // First writer wins, so category order above is the precedence rule.
    if (!words.has(key) && native !== english) words.set(key, native);
  };

  for (const category of LEXICON_CATEGORIES) {
    const entries = lexicon[category];
    if (!entries) continue;
    for (const [english, translation] of Object.entries(entries)) {
      add(english, translation.primary);
    }
  }
  // References (me/it/you) live on the profile and are needed inside values too.
  for (const [english, native] of Object.entries(profile?.references ?? {})) {
    add(english, native);
  }

  const built: ValueLexicon = { words };
  cache.set(lexicon, built);
  return built;
}

/**
 * Mask every balanced `{...}` group, then every balanced `[...]` group, then
 * every call's arguments, outermost first, so each group's interior survives
 * verbatim.
 *
 * Runs AFTER string masking, so a brace group containing a string
 * (`headers:{Authorization:\`Bearer \${$token}\`}`) holds that string's mask
 * token, and the restore pass below unwraps both layers. A scanner rather than
 * a regex because groups nest; an unbalanced `{` or `[` is left alone.
 */
function maskLiteralGroups(text: string, spans: string[]): string {
  const braces = maskGroups(text, spans, '{', '}');
  return maskCallArguments(maskGroups(braces, spans, '[', ']'), spans);
}

/** Mask every call's arguments: a `(` group touching the name before it. */
function maskCallArguments(text: string, spans: string[]): string {
  return maskGroups(text, spans, '(', ')', at => /[\w$]/.test(text[at - 1] ?? ''));
}

/**
 * Apply `rewrite` to the text outside every call's arguments, which every
 * reader takes as written (see {@link localizeValueInterior}).
 */
export function outsideCallArguments(raw: string, rewrite: (text: string) => string): string {
  if (!raw.includes('(')) return rewrite(raw);
  const spans: string[] = [];
  return restoreSpans(rewrite(maskCallArguments(raw, spans)), spans);
}

/** Mask every balanced `open … close` group, or each one `opens` accepts. */
function maskGroups(
  text: string,
  spans: string[],
  openChar: string,
  closeChar: string,
  opens: (at: number) => boolean = () => true
): string {
  let out = '';
  let index = 0;
  while (index < text.length) {
    let open = text.indexOf(openChar, index);
    while (open !== -1 && !opens(open)) open = text.indexOf(openChar, open + 1);
    if (open === -1) {
      out += text.slice(index);
      break;
    }
    let depth = 0;
    let close = -1;
    for (let i = open; i < text.length; i++) {
      if (text[i] === openChar) depth++;
      else if (text[i] === closeChar && --depth === 0) {
        close = i;
        break;
      }
    }
    if (close === -1) {
      // Unbalanced — not a literal we can reason about; leave the rest as is.
      out += text.slice(index);
      break;
    }
    spans.push(text.slice(open, close + 1));
    out += text.slice(index, open) + `${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
    index = close + 1;
  }
  return out;
}

/**
 * Restore masked spans, repeatedly: a brace-group span can itself contain a
 * string span's mask token, so one pass is not enough.
 */
function restoreSpans(text: string, spans: readonly string[]): string {
  let out = text;
  for (let pass = 0; pass < 8 && out.includes(MASK_OPEN); pass++) {
    out = out.replace(MASK_TOKEN, (_, index: string) => spans[Number(index)] ?? '');
  }
  return out;
}

/**
 * Upstream's first person, `I match .x` and `I am a Node`, in the third person
 * the lexicon has words for: `me matches .x`, `me is a Node` (both engines read
 * the pairs alike). The word pass then writes each language's own: es `yo
 * coincide .x`, where `I match .x` stayed English in every language (M2,
 * vocabulary sheet A2). The negation, `I do not match` or `X does not match`,
 * is written `not matches` (es `yo no coincide .x`), which the reader takes
 * back as `does not match` (joinExpressionTokens).
 */
const FIRST_PERSON = /(^|[^\w$.#@*:-])I\s+(match|am)\b/g;
const NOT_MATCH = /(^|[^\w$.#@*:-])(?:I\s+do|does)\s+not\s+match\b/g;
function thirdPerson(text: string): string {
  return text
    .replace(NOT_MATCH, (whole: string, lead: string) =>
      /\bI\s+do/.test(whole) ? `${lead}me not matches` : `${lead}not matches`
    )
    .replace(
      FIRST_PERSON,
      (_, lead: string, verb: string) => `${lead}me ${verb === 'match' ? 'matches' : 'is'}`
    );
}

/**
 * The `in` that scopes a query, written in the language's own word (M2,
 * vocabulary sheet A5): after a `<…/>` query, or a positional run (`first
 * .item`), and before what the reader takes as its scope there: an element
 * (`#chat`, `.modal`, `<form/>`), `closest <…>`, or a reference (`me`). es
 * `primero <input/> en cercano <form/>`. The word is masked, so the word pass
 * leaves it alone. Any other `in` (`x is in y`, a loop's) is not a query's.
 */
const QUERY_IN =
  /(<[^<>]*\/>|(?:^|[^\w$.#@*:-])(?:first|last|next|previous|random|closest)\s+[.#][\w-]+)((?:\.[\w-]+)*\s+)in(?=\s+(?:(?:closest\s+)?[#.<]|(?:me|it|you|body|document|target|result)\b))/g;
/** A query's `in` before a reference (`first .p1 in me`): the scope in its case. */
const QUERY_IN_REFERENCE =
  /(<[^<>]*\/>|(?:^|[^\w$.#@*:-])(?:first|last|next|previous|random|closest)\s+[.#][\w-]+)((?:\.[\w-]+)*\s+)in\s+(me|it|you)\b(?![\w$.'-])/g;
/** A query and its scope, for a language that writes the scope first: `[positional ]query in scope`. */
const QUERY_SCOPE =
  /(^|[^\w$.#@*:-])((?:(?:first|last|next|previous|random)\s+)?(?:<[^<>]*\/>|(?<=(?:first|last|next|previous|random)\s+)\.[\w-]+)(?:\.[\w-]+)*)\s+in\s+((?:closest\s+)?(?:<[^<>]*\/>|[#.][\w-]+)|me|it|you|body|document|target|result)(?![\w$.'-])/g;
function nativeQueryIn(text: string, words: ValueWords, spans: string[]): string {
  // The scope first, where the language writes it so (tr `bendeki ilk <p/>`):
  // its marker masked, or a reference's case form whole.
  const first = words.scopeFirst
    ? text.replace(QUERY_SCOPE, (_, lead: string, query: string, scope: string) => {
        const reference = /^(?:me|it|you)$/.test(scope) ? scope : undefined;
        const phrase = words.scopeFirst!('\u0000', reference);
        const at = phrase.indexOf('\u0000');
        spans.push(at < 0 ? phrase : phrase.slice(at + 1).trim());
        const masked = `${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
        return at < 0
          ? `${lead}${masked} ${query}`
          : `${lead}${phrase.slice(0, at)}${scope} ${masked} ${query}`;
      })
    : text;
  // `in me` in the case the `in` takes, a location (de `in mir`, M2 N3).
  const scoped = words.scopeIn
    ? first.replace(QUERY_IN_REFERENCE, (whole, query: string, gap: string, reference: string) => {
        const phrase = words.scopeIn!(reference);
        if (phrase === undefined) return whole;
        if (words.queryIn !== 'in') words.wrote?.();
        spans.push(phrase);
        return `${query}${gap}${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
      })
    : text;
  if (words.queryIn === 'in') return scoped;
  return scoped.replace(QUERY_IN, (_, query: string, gap: string) => {
    words.wrote?.();
    spans.push(words.queryIn);
    return `${query}${gap}${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
  });
}

/**
 * A conversion's `as`, in the language's own word (M2, vocabulary sheet A4):
 * es `ello como String`, where the type name stays English (TYPE_NAME). The
 * word is masked, so the word pass leaves it alone. The `a` of `is a` is not
 * a conversion's.
 */
function conversionLead(lead: string, words: ValueWords, spans: string[]): string {
  if (words.conversionAs === 'as' || !/^as\s/.test(lead)) return lead;
  words.wrote?.();
  spans.push(words.conversionAs);
  return `${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}${lead.slice(2)}`;
}

/**
 * An element-scoped name inside a value, in the language's own `element` (M2,
 * vocabulary sheet A10): `if element x is 1` is es `si elemento x es 1`, and
 * `the element's x` is written the same, as both engines read it. The word and
 * the name are masked together, so the name stays as spelled. After `element`,
 * an operator word is no name: `element is 1` reads a variable named `element`.
 * Before an index, only the possessive spelling reads (`element's bar["count"]`),
 * so there the English stays (the index is masked by then: MASK_OPEN).
 */
const ELEMENT_SCOPED =
  /(^|[^\w$.#@*:'-])(?:the\s+)?element(?:'s)?\s+([A-Za-z_]\w*)\b(?!\s*(?:\[|\uE010))/g;
const NOT_A_SCOPED_NAME: ReadonlySet<string> = new Set([
  'is',
  'am',
  'and',
  'or',
  'not',
  'no',
  'matches',
  'contains',
  'includes',
  'in',
  'as',
  'of',
  'to',
  'then',
  'else',
  'end',
  'from',
  'into',
  'with',
  'at',
  'by',
  'does',
  'do',
  'exists',
]);
function nativeElementScope(text: string, language: string, spans: string[]): string {
  const word = elementScopeWord(language);
  if (word === 'element') return text;
  return text.replace(ELEMENT_SCOPED, (whole, lead: string, name: string) => {
    if (NOT_A_SCOPED_NAME.has(name.toLowerCase())) return whole;
    spans.push(`${word} ${name}`);
    return `${lead}${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
  });
}

/**
 * The grammar words a value is written with: the `in` that scopes a query and
 * the `as` of a conversion, each the language's own or English's, and who to
 * tell when one is the language's own.
 */
export interface ValueWords {
  readonly queryIn: string;
  readonly conversionAs: string;
  readonly wrote?: () => void;
  /** `queryIn` and a reference after it, in the case it takes (de `in mir`, M2 N3), if the language has one. */
  readonly scopeIn?: (reference: string) => string | undefined;
  /**
   * A query's scope written before the query, where the language writes it so
   * (tr `bendeki <p/>`, M2 N3): the phrase for `scope` (written, or the
   * reference it is), which the query follows.
   */
  readonly scopeFirst?: ((scope: string, reference: string | undefined) => string) | undefined;
}

/** The language's own value words. */
export function nativeValueWords(language: string): ValueWords {
  return { queryIn: queryInWord(language), conversionAs: conversionAsWord(language) };
}

/**
 * Localize the interior of a value string.
 *
 * Returns the input unchanged when the language has no lexicon, so a language
 * that has not been populated degrades to English exactly as it does today.
 */
export function localizeValueInterior(
  raw: string,
  language: string,
  profile?: LanguageProfile,
  /** Words to leave as written: the expression's variables the renderer keeps (P49). */
  keep?: ReadonlySet<string>,
  valueWords: ValueWords = nativeValueWords(language)
): string {
  if (!raw) return raw;
  // No registered lexicon means the language's `lexicons/{code}` module was not
  // imported — render the interior in English, the pre-lexicon behaviour.
  const lexicon = getLexicon(language);
  if (!lexicon) return raw;
  const { words } = getValueLexicon(lexicon, profile);
  if (words.size === 0) return raw;

  const spans: string[] = [];
  const stringsMasked = raw.replace(PROTECTED_SPAN, match => {
    spans.push(match);
    return `${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
  });
  const masked = maskLiteralGroups(stringsMasked, spans).replace(
    TYPE_NAME,
    (_, lead: string, type: string) => {
      spans.push(type);
      const typeMask = `${MASK_OPEN}${spans.length - 1}${MASK_CLOSE}`;
      return `${conversionLead(lead, valueWords, spans)}${typeMask}`;
    }
  );

  const scoped = nativeElementScope(masked, language, spans);
  const localized = thirdPerson(nativeQueryIn(scoped, valueWords, spans)).replace(
    WORD,
    (whole, lead: string, word: string) => {
      if (keep?.has(word)) return whole;
      const hit = words.get(word.toLowerCase());
      return hit ? `${lead}${hit}` : whole;
    }
  );

  return restoreSpans(localized, spans);
}
